import { useState } from 'react'
import type { useStateLock } from '@/lib/hooks'
import { api } from '@/lib/api'
import { ConfirmModal } from '@/shared/components/ConfirmModal'
import { useToast } from '@/context/ToastContext'
import { formatDateTime, relativeTime } from '@/lib/format'

interface WorkspaceLockTabProps {
  orgId: string
  workspace: string
  /** Shared lock state from the parent - single source of truth, no second fetch. */
  lock: ReturnType<typeof useStateLock>
  /** Called after a successful force-unlock so the parent can refresh summaries. */
  onUnlocked: () => void
}

const ACTOR_TYPE_LABEL: Record<string, { icon: string; label: string }> = {
  vcs:       { icon: '🔁', label: 'VCS OIDC' },
  bot_token: { icon: '🤖', label: 'Bot token' },
  human:     { icon: '👤', label: 'Human' },
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: '0.58rem', color: 'var(--text3)', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontSize: '0.82rem', color: 'var(--text)' }}>{children}</span>
    </div>
  )
}

export function WorkspaceLockTab({ orgId, workspace, lock, onUnlocked }: WorkspaceLockTabProps) {
  const { showToast } = useToast()
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)

  if (lock.loading) {
    return (
      <div className="empty-state">
        <div className="spinner" />
        <p style={{ marginTop: 8, fontSize: '0.85rem', color: 'var(--text2)' }}>Loading lock status...</p>
      </div>
    )
  }

  if (lock.error) {
    return (
      <div style={{ padding: 24 }}>
        <div className="feedback-error">
          <p style={{ fontWeight: 500, marginBottom: 4 }}>Error loading lock status</p>
          <p style={{ fontSize: '0.8rem' }}>{lock.error}</p>
        </div>
      </div>
    )
  }

  const data = lock.data?.lock

  if (!data) {
    return (
      <div className="empty-state fu">
        <div className="empty-icon">🔓</div>
        <div style={{ color: 'var(--text)', fontWeight: 500 }}>Unlocked</div>
        <div style={{ fontSize: '0.78rem', color: 'var(--text3)', marginTop: 4 }}>
          No active lock on this workspace.
        </div>
      </div>
    )
  }

  const actor = data.actor_type ? ACTOR_TYPE_LABEL[data.actor_type] : null
  const location = [data.actor_city, data.actor_country].filter(Boolean).join(', ')

  async function handleForceUnlock() {
    if (!data) return
    setBusy(true)
    try {
      // Pass the exact lock_id we are showing - the API rejects with 409 if the
      // lock has rotated since this view loaded.
      await api.delete(`/api/v1/${orgId}/state/${encodeURIComponent(workspace)}/lock`, { ID: data.lock_id })
      lock.refetch()
      onUnlocked()
      showToast('Lock released.', 'success')
    } catch (err) {
      showToast((err as Error).message ?? 'Force unlock failed', 'error')
    } finally {
      setBusy(false)
      setConfirming(false)
    }
  }

  return (
    <div className="fu" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {confirming && (
        <ConfirmModal
          track="state_force_unlock_confirm"
          title="Force unlock this workspace?"
          message="If a run still holds this lock, releasing it lets another apply write state at the same time, which can corrupt it. Only do this once you know the run has stopped."
          confirmLabel="Force unlock"
          busy={busy}
          onConfirm={handleForceUnlock}
          onCancel={() => setConfirming(false)}
        />
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span className="badge badge-amber" style={{ fontSize: '0.7rem' }}>🔒 Locked</span>
        <span style={{ fontSize: '0.78rem', color: 'var(--text3)' }}>
          locked {relativeTime(data.created_at)}
        </span>
        <div style={{ flex: 1 }} />
        <button
          className="btn btn-ghost btn-sm"
          style={{ color: 'var(--amber)', borderColor: 'var(--border-amber)' }}
          disabled={busy}
          onClick={() => setConfirming(true)}
          title="Release this lock"
        >
          {busy ? <span className="spinner" /> : 'Force unlock'}
        </button>
      </div>

      <div
        style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16,
          padding: 16, background: 'var(--bg2)', borderRadius: 8, border: '1px solid var(--border)',
        }}
      >
        <Field label="Who">
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem' }}>{data.who}</span>
        </Field>
        <Field label="Operation">
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.78rem' }}>{data.operation}</span>
        </Field>
        <Field label="Lock ID">
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.72rem', color: 'var(--text2)' }}>{data.lock_id}</span>
        </Field>
        <Field label="Acquired">
          {formatDateTime(data.created_at)}
        </Field>

        <Field label="Actor">
          {data.actor_name ?? data.who}
        </Field>
        <Field label="Auth type">
          {actor
            ? <span className="badge badge-neutral" style={{ fontSize: '0.65rem' }}>{actor.icon} {actor.label}</span>
            : <span className="badge badge-neutral" style={{ fontSize: '0.65rem' }}>❔ Unknown (legacy lock)</span>}
        </Field>
        <Field label="Location">
          {location || 'Unknown'}
        </Field>
        <Field label="Reported IP">
          <span style={{ fontFamily: 'var(--mono)', fontSize: '0.76rem', color: 'var(--text2)' }}>{data.actor_ip ?? '—'}</span>
        </Field>
      </div>

      <div style={{ fontFamily: 'var(--mono)', fontSize: '0.62rem', color: 'var(--text3)' }}>
        // Reported IP is the source IP seen at lock time and is informational only.
      </div>
    </div>
  )
}
